import {
  TrueBase,
  css,
  html,
  debounce,
} from "../TrueBase/TrueBase";
import { cache } from "lit-html/directives/cache.js";

/**
 * @customelement - true-list-box
 * @description
 * Component that renders items in list form (horizontal and vertical)
 *
 * ### Properties
 * @prop - {String} direction - direction of the list (horizontal, vertical);
 *
 */

const collapseView = (context) => html`
  <button class="collapse-trigger" @click="${context.collapseToggle}">
    <slot name="title"></slot>
    <span>
      <svg
        class="collapse-icon"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        x="0px"
        y="0px"
        viewBox="0 0 490.688 490.688"
        style="enable-background:new 0 0 490.688 490.688;"
        width="15"
        xml:space="preserve"
      >
        <path
          d="M472.328,120.529L245.213,347.665L18.098,120.529c-4.237-4.093-10.99-3.975-15.083,0.262
                          c-3.992,4.134-3.992,10.687,0,14.82l234.667,234.667c4.165,4.164,10.917,4.164,15.083,0l234.667-234.667
                          c4.237-4.093,4.354-10.845,0.262-15.083c-4.093-4.237-10.845-4.354-15.083-0.262c-0.089,0.086-0.176,0.173-0.262,0.262
                          L472.328,120.529z"
        />
        <path
          d="M245.213,373.415c-2.831,0.005-5.548-1.115-7.552-3.115L2.994,135.633c-4.093-4.237-3.975-10.99,0.262-15.083
                          c4.134-3.992,10.687-3.992,14.82,0l227.136,227.115l227.115-227.136c4.093-4.237,10.845-4.354,15.083-0.262
                          c4.237,4.093,4.354,10.845,0.262,15.083c-0.086,0.089-0.173,0.176-0.262,0.262L252.744,370.279
                          C250.748,372.281,248.039,373.408,245.213,373.415z"
        />
      </svg>
    </span>
  </button>
  <div class="collapse-body" role="list">
    <slot></slot>
  </div>
  <div class="collapse-line"></div>
`;

const normalView = () => html`
  <ul class="tp-content-wrapper" role="list">
    <slot name="title"></slot>
    <slot></slot>
  </ul>
`;

export class TrueListBox extends TrueBase {
  static get properties() {
    return {
      direction: {
        type: String,
      },
      collapseOnMobileBreakpoint: {
        type: Number,
      },
      collapsedList: {
        type: Boolean,
      },
      notCollapsed: {
        type: Boolean,
        reflect: true,
      },
    };
  }

  constructor() {
    super();
  }

  update() {
    super.update();
    this.checkView();
  }

  firstUpdated() {
    const checkDebounce = debounce(this.checkView.bind(this));
    window.addEventListener("resize", checkDebounce);
  }

  checkView() {
    const windowWidth = window.innerWidth;
    if (windowWidth < this.collapseOnMobileBreakpoint) {
      this.collapsedList = true;
    } else {
      this.collapsedList = false;
    }
  }

  collapseToggle() {
    this.notCollapsed = !this.notCollapsed;
  }

  render() {
    return this.collapsedList ? collapseView(this) : normalView();
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          margin: var(--list-box-margin, 1rem 0);
          display: block;
        }

        .tp-content-wrapper {
          display: var(--list-box-display, inline-flex);
          flex-wrap: var(--list-box-flex-wrap, wrap);
        }

        ul {
          margin: var(--list-box-ul-margin, 0);
          list-style: none;
          padding: var(--list-box-ul-padding, 0);
        }

        ::slotted([slot="title"]) {
          font-size: var(--list-box-title-font-size, 1.15rem);
          text-transform: var(--list-box-title-text-transform, none);
          color: var(--list-box-title-color, black);
          margin: var(--list-box-title-margin, 1rem 0);
          font-weight: var(--list-box-title-font-weight, bold);
          display: block;
        }

        .collapse-trigger {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          cursor: pointer;
          color: var(--list-box-collapse-trigger-color, black);
          border: var(--list-box-collapse-trigger-border, none);
          background-color: var(
            --list-box-collapse-trigger-background-color,
            transparent
          );
          padding: var(--list-box-collapse-trigger-padding, 0 0 0.5em 0);
        }

        .collapse-trigger:focus {
          outline: none;
        }

        .collapse-body {
          display: none;
        }

        .collapse-icon path {
          transition: transform 0.3s ease;
          fill: var(--list-box-collapse-icon-color, black);
        }

        .collapse-line {
          height: 1px;
          background-color: var(
            --list-box-collapse-line-background-color,
            rgba(12, 11, 49, 0.16)
          );
          width: 100%;
        }

        :host([notCollapsed]) .collapse-body {
          display: block;
          padding-bottom: 1rem;
        }

        :host([notCollapsed]) .collapse-icon {
          transform: rotate(-180deg);
        }

        ::slotted(*:not(:first-child)) {
          margin-left: var(--list-box-item-horizontal-spacing, 2rem);
        }

        :host([direction="vertical"]) .tp-content-wrapper {
          flex-direction: column;
        }

        :host([direction="vertical"]) ::slotted(*:not(:first-child)) {
          margin-top: var(--list-box-item-vertical-spacing, 1rem);
          display: block;
          margin-left: 0;
        }

        @supports (-ms-ime-align: auto) {
          true-link {
            margin-left: var(--list-box-item-horizontal-spacing, 16px);
          }
        }

        @media screen and (-ms-high-contrast: active),
          screen and (-ms-high-contrast: none) {
          /* IE10+ specific styles go here */
          true-link {
            margin-left: var(--list-box-item-horizontal-spacing, 16px);
          }
        }
      `,
    ];
  }
}

window.customElements.get("true-list-box") ||
  window.customElements.define("true-list-box", TrueListBox);
